import { useEffect, useRef, useState } from 'react';
import { Fade, Form } from 'react-bootstrap';
import Calendar from 'react-calendar';
import { createUseStyles } from 'react-jss';
import { isElementXPercentInViewport, parseISOString } from '../../utils';

import 'react-calendar/dist/Calendar.css';

/**
 * CampoDate es un datepicker personalizado, utiliza la librería react-calendar para renderizar un calendario
 * al seleccionar el campo correspondiente. Permite setear maxima y minima fecha, formato de fecha, callbaks
 * y la opción de solo trabajar con meses. Ver ejemplo de uso en secciones del cv y en compUtils.
 */

const useStyles = createUseStyles({
  calendarContainer: {
    outline: 'none',
    position: 'absolute',
    zIndex: 1000,
    '& .react-calendar': {
      width: '320px',
    },
    '& .react-calendar__year-view .react-calendar__tile': {
      padding: '1em .5em',
    },
  },
  onTop: {
    top: -1,
    transform: 'translateY(-100%)',
  },
  onBottom: {
    bottom: -1,
    transform: 'translateY(100%)',
  },
  formGroup: {
    position: 'relative',
  },
  calendarClose: {
    paddingTop: 15,
  },
  closeBtn: {
    position: 'absolute',
  },
});
const CampoDate = ({
  id = null,
  format = 'dd/mm/yyyy',
  selectedDate = null,
  month = false,
  onChange = null,
  onBlur = null,
  maxDate = undefined,
  minDate = undefined,
  disabled = false,
  ...props
}) => {
  const [date, setDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [location, setLocation] = useState('top');
  const [blurEvent, setBlurEvent] = useState(null);

  const classes = useStyles();

  const viewportRef = useRef();

  let calendarTimeout,
    calendarFocused = false;

  useEffect(() => {
    let value = selectedDate || null;
    setDate(value);
  }, [selectedDate]);

  const handleDate = (date) => {
    setDate(date);
    setShowCalendar(false);
    onChange?.(date);
  };

  const checkViewport = (el) => {
    const isIn = isElementXPercentInViewport(el, 100);
    if (!isIn) {
      setLocation(location === 'bottom' ? 'top' : 'bottom');
    }
  };
  const handleOnFocus = () => {
    setShowCalendar(true);
  };
  const handleOnBlur = (ev) => {
    if (!calendarFocused) setShowCalendar(false);
    ev.persist();
    setBlurEvent(ev);
  };
  const handleChange = () => {
    onChange?.(date);
  };

  const handleExitedCalendar = () => {
    calendarFocused = false;
    onBlur && blurEvent && onBlur(blurEvent);
  };

  const onCalendarBlur = (ev) => {
    calendarTimeout = setTimeout(() => {
      setShowCalendar(false);
    }, 0);
  };

  const onCalendarFocus = (ev) => {
    calendarTimeout && clearTimeout(calendarTimeout);
  };
  const handleKeyUp = (ev) => {
    if (ev.keyCode === 8 || ev.keyCode === 46) {
      //Tecla de borrado o suprimir
      setShowCalendar(false);
      onChange?.('');
    }
  };

  return (
    <>
      <div
        onBlur={onCalendarBlur}
        onFocus={onCalendarFocus}
        onKeyUp={handleKeyUp}
        className={`${location === 'bottom' ? classes.onBottom : classes.onTop} ${
          classes.calendarContainer
        }`}
        onMouseDown={() => (calendarFocused = true)}
        ref={viewportRef}>
        <Fade
          in={showCalendar}
          unmountOnExit
          onEnter={() => checkViewport(viewportRef.current)}
          onExited={handleExitedCalendar}>
          <Calendar
            className={classes.calendarClose}
            locale="es-AR"
            maxDetail={month ? 'year' : 'month'}
            value={date}
            onChange={(date) => {
              handleDate(date);
            }}
            onClickDecade={onCalendarFocus}
            onClickYear={onCalendarFocus}
            minDate={typeof minDate === 'string' ? parseISOString(minDate) : minDate}
            maxDate={typeof maxDate === 'string' ? parseISOString(maxDate) : maxDate}
          />
        </Fade>
      </div>
      <Form.Control
        id={id}
        autoComplete="off"
        onBlur={handleOnBlur}
        onChange={handleChange}
        onFocus={handleOnFocus}
        disabled={disabled}
        {...props}
      />
    </>
  );
};

export default CampoDate;
